import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink, FileText, Shield, ScrollText } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { LegalDocument } from "@/hooks/useLegalDocuments";

interface LegalContentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: "privacy" | "terms";
}

export const LegalContentDialog = ({ 
  open, 
  onOpenChange, 
  initialTab = "privacy" 
}: LegalContentDialogProps) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<"privacy" | "terms">(initialTab);
  const { loading: loadingTerms, currentSetting: termsSetting } = useAppSettings('terms_and_conditions');
  const { loading: loadingPrivacy, currentSetting: privacySetting } = useAppSettings('privacy_policy');
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);

  // Fetch active legal documents
  useEffect(() => {
    const fetchLegalDocuments = async () => {
      try {
        const { data, error } = await supabase
          .from('legal_documents')
          .select('*')
          .eq('is_active', true)
          .order('display_name');

        if (error) {
          console.error('[LEGAL-DIALOG] Error fetching legal documents:', error);
        } else {
          setLegalDocuments(data || []);
        }
      } catch (error) {
        console.error('[LEGAL-DIALOG] Error fetching legal documents:', error);
      } finally {
        setLoadingDocuments(false);
      }
    };

    if (open) {
      fetchLegalDocuments();
    }
  }, [open]);

  // Reset to initial tab when dialog opens
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  const handleDocumentClick = (fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  const isLoading = loadingTerms || loadingPrivacy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`glass-effect max-w-2xl max-h-[85vh] flex flex-col ${isMobile ? 'mx-4 my-8' : ''}`}>
        <DialogHeader className="space-y-3 pb-4 border-b border-border/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl font-medium text-card-foreground">
                Legal Information
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                Review our policies and legal documents
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="flex gap-2 pt-4 border-b border-border/20">
          <button
            onClick={() => setActiveTab("privacy")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === "privacy"
                ? "bg-primary/10 text-primary border-b-2 border-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            <Shield className="h-4 w-4" />
            Privacy Policy
          </button>
          <button
            onClick={() => setActiveTab("terms")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === "terms"
                ? "bg-primary/10 text-primary border-b-2 border-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            <ScrollText className="h-4 w-4" />
            Terms & Conditions
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-3 text-sm text-muted-foreground">Loading content...</span>
            </div>
          ) : (
            <>
              {activeTab === "privacy" && (
                <>
                  {privacySetting?.value ? (
                    <div className="space-y-3">
                      <div className="bg-muted/30 rounded-lg p-4 border border-border/20">
                        <div 
                          dangerouslySetInnerHTML={{ __html: privacySetting.value }} 
                          className="prose prose-sm max-w-none text-muted-foreground dark:prose-invert [&>*]:mb-3 [&>*:last-child]:mb-0" 
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Shield className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                      <p className="text-muted-foreground">
                        Privacy policy content has not been configured yet.
                      </p>
                    </div>
                  )}
                </>
              )}

              {activeTab === "terms" && (
                <>
                  {termsSetting?.value ? (
                    <div className="space-y-3">
                      <div className="bg-muted/30 rounded-lg p-4 border border-border/20">
                        <div 
                          dangerouslySetInnerHTML={{ __html: termsSetting.value }} 
                          className="prose prose-sm max-w-none text-muted-foreground dark:prose-invert [&>*]:mb-3 [&>*:last-child]:mb-0" 
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <ScrollText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                      <p className="text-muted-foreground">
                        Terms and conditions content has not been configured yet.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Additional Documents Section - shown on both tabs */}
              {!loadingDocuments && legalDocuments.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-border/20">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-primary" />
                    <h4 className="font-medium text-card-foreground">Additional Documents</h4>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/20">
                    <div className="grid gap-2">
                      {legalDocuments.map((document) => (
                        <button
                          key={document.id}
                          onClick={() => handleDocumentClick(document.file_url)}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-accent/50 transition-colors text-left group"
                        >
                          <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-sm text-card-foreground group-hover:text-primary transition-colors">
                            {document.display_name}
                          </span>
                          <ExternalLink className="h-3 w-3 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex-shrink-0 pt-4 border-t border-border/20">
          <Button 
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LegalContentDialog;
